<template>
  <v-dialog v-model="dialog" fullscreen hide-overlay transition="dialog-bottom-transition"
            @click:outside="$emit('close', false)">
    <v-card>
      <v-app-bar dark color="primary" fixed absolute elevation="4" class="fixed-toolbar">
        <v-btn icon dark @click="$emit('close', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>{{ $t(`discount.title.${edit ? 'update' : 'create'}`, { name: form.name }) }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn dark text :loading="loading" :disabled="!submittable" @click="submitForm">
          {{ $t(`discount.button.${edit ? 'update' : 'create'}`) }}
        </v-btn>
      </v-app-bar>

      <v-container fluid class="toolbar-padding">
        <v-row justify="center">
          <v-col cols="12" md="8" lg="6">
            <v-card class="elevation-0 mb-6">
              <v-card-text>
                <v-form ref="discountForm" v-model="isFormValid" lazy-validation>
                  <v-row>
                    <v-col cols="12">
                      <v-text-field
                        ref="inputName"
                        v-model="form.name"
                        :label="$t('discount.form.name')"
                        :rules="nameRules"
                        required
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-radio-group
                        v-model="form.type"
                        :label="$t('discount.form.type')"
                        :rules="typeRules"
                        row
                      >
                        <v-radio
                          v-for="type in types"
                          :key="type.value"
                          :label="type.label"
                          :value="type.value"
                        ></v-radio>
                      </v-radio-group>
                    </v-col>
                    <v-col cols="12">
                      <v-text-field
                        ref="valueField"
                        v-model="form.value"
                        :label="$t('discount.form.value')"
                        :rules="valueRules"
                        type="number"
                        :suffix="form.type === 'percentage' ? '%' : ''"
                        :min="minValue"
                        :max="maxValue"
                        required
                        outlined
                        dense
                        @focus="handleValueFieldFocus"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-form>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snackbar" :timeout="3000" :color="snackbarColor" bottom right>
      <v-icon left>{{ snackbarIcon }}</v-icon>
      {{ snackbarText }}
    </v-snackbar>
  </v-dialog>
</template>

<script>
export default {
  name: 'createDiscount',
  props: {
    display: {
      type: Boolean,
      required: true
    },
    discount: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  data () {
    return {
      dialog: this.display,
      form: {
        name: '',
        type: 'percentage',
        value: ''
      },
      types: [
        {
          label: this.$t('discount.label.amount'),
          value: 'amount'
        },
        {
          label: this.$t('discount.label.percentage'),
          value: 'percentage'
        }
      ],
      loading: false,
      isFormValid: true,

      // Validation rules
      nameRules: [
        v => !!v || 'Discount name is required',
        v => v.length >= 3 || 'Discount name must be at least 3 characters'
      ],
      valueRules: [
        v => !!v || 'Discount value is required',
        v => (this.form.type === 'percentage' ? (v >= 0.01 && v <= 100) : v >= 0) ||
             (this.form.type === 'percentage' ? 'Percentage must be between 0.01 and 100' : 'Amount must be greater than or equal to 0')
      ],
      typeRules: [
        v => !!v || 'Discount type is required'
      ],

      // Snackbar
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      snackbarIcon: 'mdi-check-circle'
    }
  },
  watch: {
    display (val) {
      this.dialog = val
    },
    dialog (val) {
      if (!val) {
        this.$emit('close', false)
      } else {
        this.initializeForm()
      }
    },
    'form.type' (_) {
      this.form.value = this.minValue
    }
  },
  computed: {
    submittable () {
      return this.isFormValid && this.form.name && this.form.value && this.form.type
    },
    edit () {
      return this.discount && this.discount.id
    },
    maxValue () {
      return this.form.type === 'amount' ? 99999999999999 : 100
    },
    minValue () {
      return this.form.type === 'percentage' ? 0.01 : 0
    }
  },
  methods: {
    initializeForm () {
      if (this.edit) {
        this.form.name = this.discount.name || ''
        this.form.value = this.discount.value || this.minValue
        this.form.type = this.discount.type || 'percentage'
      } else {
        this.resetForm()
      }

      // Focus on name input after initialization
      this.$nextTick(() => {
        if (this.$refs.inputName) {
          this.$refs.inputName.focus()
        }
      })
    },

    resetForm () {
      this.form = {
        name: '',
        type: 'percentage',
        value: this.minValue
      }

      if (this.$refs.discountForm) {
        this.$refs.discountForm.resetValidation()
      }
    },

    showSnackbar (text, type = 'success') {
      this.snackbarText = text
      this.snackbarColor = type
      this.snackbarIcon = type === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle'
      this.snackbar = true
    },

    handleValueFieldFocus () {
      if (this.form.value <= 0) {
        // For Vuetify text fields, we need to use the input element
        this.$nextTick(() => {
          const inputEl = this.$refs.valueField.$el.querySelector('input')
          if (inputEl) {
            inputEl.select()
          }
        })
      }
    },

    async submitForm () {
      if (this.$refs.discountForm.validate()) {
        this.askQuestion(this.$t(`discount.confirm.${this.edit ? 'update' : 'create'}`), async () => {
          this.loading = true
          try {
            let data
            if (this.discount.id) {
              data = await this.$discountService.updateDiscount(this.discount.id, this.form)
              this.showSnackbar(this.$t('discount.message.updated'))
              this.$emit('updated', data)
            } else {
              data = await this.$discountService.createDiscount(this.form)
              this.showSnackbar(this.$t('discount.message.created'))
              this.$emit('created', data)
            }
            this.$emit('discount-ok')
            this.resetForm()
            this.$emit('close', false)
          } catch (error) {
            // Error is already handled by the global interceptor
            console.log('Component caught error in submitForm:', error)
          } finally {
            this.loading = false
          }
        })
      }
    }
  },
  mounted () {
    this.initializeForm()
  }
}
</script>

<style>
/* Add padding to account for fixed toolbar */
.toolbar-padding {
  padding-top: 64px; /* Height of the toolbar */
}

/* Fixed toolbar styles */
.fixed-toolbar {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  z-index: 9999 !important;
  width: 100% !important;
}

/* Ensure proper spacing on mobile */
@media (max-width: 599px) {
  .toolbar-padding {
    padding-top: 56px; /* Smaller toolbar height on mobile */
  }
}
</style>
